import { Response } from './index';
import { mgmtAxios, MgmtEnvelope, getMgmtMemberToken } from './mgmt';

/**
 * PG-56 外循環歸還:全面改打新版後端(package-plus-management-backend)。
 *
 * 這支是 adapter —— 保留舊版對外函式簽名/回傳形狀,讓 return.tsx 改動最小,
 * 內部改呼叫新版端點。舊版 vipAxios(REACT_APP_API_URL)+ HMAC 全部移除。
 *
 * 對映:
 *  - getChannelStoreBySC → POST /api/public/return/channel-store
 *  - getEpkType / chkEpkId → GET /api/public/packaging/{id}
 *  - chkOtherUser → GET /api/member/rentals/return-for/lookup
 *  - saveUserRentLogs → POST /api/member/rentals/return
 *  - saveGuestRentLogs → POST /api/guest/rentals/return
 *  - checkPendingCouponClaim → POST /api/member/rentals/pending-reward
 *  - bindUserRentLog → POST /api/member/rentals/claim
 *  - 領券/儲值券已改點數(歸還/綁定時自動發點),updateUserRentLogs/updateUserVoucherLogs 保留為 no-op。
 *
 * momo 分流:chkEpkId 回傳後端 packaging.customer(PG-181 已補),
 * customer='momo' → 免登入即完成;null → 訪客綁定/LIFF 登入流程。
 */

const authHeader = (token?: string | null) =>
  token ? { Authorization: `Bearer ${token}` } : undefined;

/**
 * 新後端的重複歸還防護回 409 + details.error_code;舊版是 200 + 這幾個英文字串,
 * return.tsx 一直是照字串分流顯示中文。這裡把 code 轉回同一組字串塞進
 * e.response.data.errors,頁面既有邏輯不用重寫。
 */
const LEGACY_DUPLICATE_MESSAGES: Record<string, string> = {
  rental_already_returned: 'Returned twice on the same day.',
  return_limit_exceeded: 'The number of returns exceeds three times.',
  return_too_frequent: 'Return frequency is too high.',
};

export const duplicateReturnMessage = (err?: string | null): string | null => {
  switch (err) {
    case 'Return frequency is too high.':
      return '若您取得多個同包裝編號的循環包裝，請稍候一分鐘再試。';
    case 'The number of returns exceeds three times.':
      return '同包裝編號的循環包裝歸還次數超過上限，請隔日歸還。';
    case 'Returned twice on the same day.':
      return '您輸入的包裝編號已在系統中成功歸還，請將包裝交給店員或投入歸還箱。';
    default:
      return null;
  }
};

const rethrowWithLegacyMessage = (e: any): never => {
  const code = e?.response?.data?.details?.error_code;
  const legacy =
    e?.response?.status === 409 && code ? LEGACY_DUPLICATE_MESSAGES[code] : undefined;
  if (legacy && e.response) {
    e.response.data = { ...(e.response.data ?? {}), errors: legacy, message: legacy };
  }
  throw e;
};

// ---------------- 通路/店家查詢 ----------------

export interface QRCode1ApiData {
  channel_name: string;
  channel_id: number;
  use_type: number;
  svpt_name: string;
  svpt_id: string | number;
  chs_short_code: string;
  store_id: string;
  return_type: 'box' | 'staff';
  is_fami?: boolean;
}
type qry1FormData = {
  chs_short_code?: string;
  store_id?: string;
  lihi_url?: string;
};

export const getChannelStoreBySC = async (
  _lineToken: string,
  _brandID: number,
  formData: qry1FormData
): Promise<Response<QRCode1ApiData>> => {
  const { data } = await mgmtAxios.request<MgmtEnvelope<{
    channel_name: string;
    channel_id: number;
    chs_short_code: string;
    return_type: 'box' | 'staff';
    is_fami: boolean;
    store_id: string | null;
    svpt_id: string | null;
    svpt_name: string | null;
  }>>({
    url: `/api/public/return/channel-store`,
    method: 'POST',
    data: {
      chs_short_code: formData.chs_short_code,
      store_id: formData.store_id,
    },
  });
  const d = data.details;
  return {
    data: {
      channel_name: d.channel_name,
      // 後端有回真實 channel_id(全家=1)。之前寫死 0 → onManualConfirm 的
      // `!channelIdRef.current` 守門永遠成立 → 手動輸入每次提早 return,輸入框不關 → 無限 loop。
      channel_id: d.channel_id ?? 0,
      use_type: 3,
      svpt_name: d.svpt_name ?? '',
      svpt_id: d.svpt_id ?? '',
      chs_short_code: d.chs_short_code,
      store_id: d.store_id ?? (formData.store_id ?? ''),
      return_type: d.return_type,
      is_fami: d.is_fami,
    },
  };
};

// ---------------- 包材類型 / 查包裝 ----------------

interface PackagingLookup {
  id: string;
  sku?: { id: string; name: string } | null;
  updated_at?: string;
  customer?: string | null;
}

const fetchPackaging = async (epkId: string): Promise<PackagingLookup> => {
  const { data } = await mgmtAxios.request<MgmtEnvelope<PackagingLookup>>({
    url: `/api/public/packaging/${encodeURIComponent(epkId)}`,
    method: 'GET',
  });
  return data.details;
};

export interface QRCode2ApiData {
  type_name: string;
  date_time: string;
}
type qry2FormData = { epk_id?: string };

export const getEpkType = async (
  _lineToken: string,
  _brandID: number,
  formData: qry2FormData
): Promise<Response<QRCode2ApiData>> => {
  const p = await fetchPackaging(formData.epk_id ?? '');
  return {
    data: {
      type_name: p.sku?.name ?? '循環包裝',
      date_time: p.updated_at ?? '',
    },
  };
};

export interface ChkEpkIdData {
  id: number;
  epk_id: string;
  type: string;
  customer: string | null;
}
type qry4FormData = { epk_id?: string; is_guest: number };

export const chkEpkId = async (
  _lineToken: string,
  _brandID: number,
  formData: qry4FormData
): Promise<Response<ChkEpkIdData>> => {
  const p = await fetchPackaging(formData.epk_id ?? '');
  return {
    data: {
      id: 0,
      epk_id: p.id,
      type: p.sku?.name ?? '循環包裝',
      // PG-181:回傳後端 packaging.customer,momo 走免登入完成、null 走綁定流程
      customer: p.customer ?? null,
    },
  };
};

// ---------------- 代還他人 ----------------

export interface OtherUserData {
  id: number;
  brand_id: number;
  realname: string;
  nickname: string;
  mobile_number: string;
  email: string;
}
type qry3FormData = { mobile_number?: string };

export const chkOtherUser = async (
  lineToken: string,
  _brandID: number,
  formData: qry3FormData
): Promise<Response<OtherUserData>> => {
  const token = getMgmtMemberToken();
  const { data } = await mgmtAxios.request<MgmtEnvelope<{
    id: number;
    realname: string | null;
    nickname: string | null;
    mobile_number: string;
    email: string | null;
  }>>({
    url: `/api/member/rentals/return-for/lookup`,
    method: 'GET',
    params: { mobile_number: formData.mobile_number },
    headers: authHeader(token),
  });
  const d = data.details;
  return {
    data: {
      id: d.id,
      brand_id: 0,
      realname: d.realname ?? '',
      nickname: d.nickname ?? '',
      mobile_number: d.mobile_number,
      email: d.email ?? '',
    },
  };
};

// ---------------- 歸還紀錄 ----------------

export interface Logs1Message {
  success: boolean;
  message: string;
  userRentLog_id: number;
  user_rent_logs: object;
  svpt_info: string;
  userVoucherLog_id: number;
  /** PG-209 這次是重掃同一顆包裝,後端沒寫新紀錄,回的是稍早那筆 */
  is_rescan?: boolean;
  data?: {
    message: string;
    success: boolean;
    user_rent_log_id?: number;
    claim_token?: string;
    customer?: string | null;
  };
}

/**
 * 歸還地點:掃歸還點 QR 得到的通路代號與門市店號。
 *
 * return_location_id 只落得到後端 tb_locations,全家等通路門市查不到會 fallback 到主倉庫,
 * 後台歸還紀錄因此看不出「哪個通路的哪家門市收的」。這兩欄是補那一欄用的,後端 optional。
 */
type ReturnPlace = {
  chs_short_code?: string;
  store_id?: string;
};

/**
 * 「選了全家但還沒指定門市」的 placeholder 店號。全家是要號-first、交件到任一門市,
 * 所以路由上固定帶這個值,它不是真的店號。
 */
export const FAMI_STORE_PLACEHOLDER = 'fami99999';

/**
 * 把畫面上的通路/店號整理成可送出的歸還地點。
 *
 * 空字串與全家 placeholder 都要濾掉 —— 送出去只會在後台歸還紀錄變成空店號或假店號,
 * 比留白更難查。
 */
export const buildReturnPlace = (
  shortCode?: string | null,
  storeId?: string | null
): ReturnPlace => ({
  chs_short_code: shortCode || undefined,
  store_id: storeId && storeId !== FAMI_STORE_PLACEHOLDER ? storeId : undefined,
});

const returnPlaceFields = (place: ReturnPlace) =>
  buildReturnPlace(place.chs_short_code, place.store_id);

type Logs1FormData = ReturnPlace & {
  epk_id?: string;
  renter_id?: number;
  return_user_id?: number;
  chs_id?: number;
  svpt_id?: string | number;
  return_type?: number;
};

/** 會員歸還 → POST /api/member/rentals/return */
export const saveUserRentLogs = async (
  lineToken: string,
  _brandID: number,
  formData: Logs1FormData
): Promise<Response<Logs1Message>> => {
  const token = getMgmtMemberToken();
  let data: MgmtEnvelope<{ id: number; is_rescan?: boolean }>;
  try {
    ({ data } = await mgmtAxios.request<MgmtEnvelope<{ id: number; is_rescan?: boolean }>>({
      url: `/api/member/rentals/return`,
      method: 'POST',
      data: {
        packaging_id: formData.epk_id,
        // 幫別人還:被代還會員的 id(從輸入手機頁 lookup 而來),後端才會把歸還單掛到對方名下
        renter_id: formData.renter_id,
        return_location_id:
          typeof formData.svpt_id === 'string' ? formData.svpt_id : undefined,
        return_type: 'scan',
        ...returnPlaceFields(formData),
      },
      headers: authHeader(token),
    }));
  } catch (e) {
    return rethrowWithLegacyMessage(e);
  }
  return {
    data: {
      success: true,
      message: data.status,
      userRentLog_id: data.details.id,
      user_rent_logs: data.details,
      svpt_info: '',
      userVoucherLog_id: 0,
      // PG-209 重掃同一顆包裝:後端不寫第二筆、回原本那筆,畫面要說明這是稍早的編號
      is_rescan: data.details.is_rescan === true,
      data: { message: data.status, success: true, user_rent_log_id: data.details.id },
    },
  };
};

type GuestFormData = ReturnPlace & {
  epk_id?: string;
  chs_id?: number;
  svpt_id?: string | number;
};

/**
 * 訪客歸還當下存下來的 claim_token(uuid)。重掃時一起送給後端,
 * 後端才認得出「這是同一個人的同一次歸還」而不是另一顆包裝。
 * 舊版曾把數字歸還單 id 存進同一個 key,非 uuid 一律不送。
 */
const storedGuestClaimToken = (): string | undefined => {
  try {
    const raw = localStorage.getItem('pending_coupon_claim');
    if (!raw) return undefined;
    const v = JSON.parse(raw)?.user_rent_log_id;
    return typeof v === 'string' && v.length === 36 ? v : undefined;
  } catch (e) {
    return undefined;
  }
};

/** 訪客歸還 → POST /api/guest/rentals/return(回 claim_token 供登入後綁定發點) */
export const saveGuestRentLogs = async (
  formData: GuestFormData
): Promise<Response<Logs1Message>> => {
  let data: MgmtEnvelope<{ rental: { id: number; is_rescan?: boolean }; claim_token: string }>;
  try {
    ({ data } = await mgmtAxios.request<MgmtEnvelope<{
      rental: { id: number; is_rescan?: boolean };
      claim_token: string;
    }>>({
      url: `/api/guest/rentals/return`,
      method: 'POST',
      data: {
        packaging_id: formData.epk_id,
        return_location_id:
          typeof formData.svpt_id === 'string' ? formData.svpt_id : undefined,
        claim_token: storedGuestClaimToken(),
        ...returnPlaceFields(formData),
      },
    }));
  } catch (e) {
    return rethrowWithLegacyMessage(e);
  }
  const d = data.details;
  return {
    data: {
      success: true,
      message: data.status,
      userRentLog_id: d.rental.id,
      user_rent_logs: d.rental,
      svpt_info: '',
      userVoucherLog_id: 0,
      is_rescan: d.rental.is_rescan === true,
      data: {
        message: data.status,
        success: true,
        user_rent_log_id: d.rental.id,
        claim_token: d.claim_token,
        customer: null,
      },
    },
  };
};

// ---------------- 領券/儲值券 → 已改點數,保留 no-op ----------------

export interface Logs2Message {
  success: boolean;
  message: string;
  user_rent_logs: object;
}
type Logs2FormData = { id?: number; userRentLog_id?: number; coupon_id?: number[] };

/** 舊版歸還後領券;新後端歸還時自動發點,這裡 no-op 保持相容 */
export const updateUserRentLogs = async (
  _lineToken: string,
  _brandID: number,
  _formData: Logs2FormData
): Promise<Response<Logs2Message>> => {
  return { data: { success: true, message: '獎勵已於歸還時發放點數', user_rent_logs: {} } };
};

export interface Logs3Message {
  success: boolean;
  message: string;
  userVoucherLog_id: number;
  user_voucher_logs: object;
}
type Logs3FormData = { id?: number; userVoucherLog_id?: number };

/** 舊版儲值券;新後端改點數,no-op */
export const updateUserVoucherLogs = async (
  _lineToken: string,
  _brandID: number,
  _formData: Logs3FormData
): Promise<Response<Logs3Message>> => {
  return {
    data: { success: true, message: '獎勵已改為點數', userVoucherLog_id: 0, user_voucher_logs: {} },
  };
};

// ---------------- momo 事後綁定領獎勵(券→點) ----------------

export interface PendingCouponClaimCoupon {
  id: number;
  title: string;
  description?: string;
  picture_url?: string;
  start_at?: string | null;
  expired_at?: string | null;
  quantity_left?: number;
}

export interface PendingCouponClaimData {
  claimable: boolean;
  coupons: PendingCouponClaimCoupon[];
  user_rent_log_id?: number | null;
}

/**
 * 查歸還後可領的優惠券(PG-178 Neil)→ GET /api/member/coupons/available。
 * 會員全站可領清單(不需帶 id);排除已領。
 */
export const checkPendingCouponClaim = async (
  _lineToken: string,
  _claimToken?: string
): Promise<Response<PendingCouponClaimData>> => {
  const token = getMgmtMemberToken();
  const { data } = await mgmtAxios.request<MgmtEnvelope<{
    id: number;
    campaign_name: string;
    title: string;
    image_url: string | null;
    use_start_at: string | null;
    use_end_at: string | null;
  }[]>>({
    url: `/api/member/coupons/available`,
    method: 'GET',
    headers: authHeader(token),
  });
  const coupons = (data.details || []).map((c) => ({
    id: c.id,
    title: c.title,
    description: c.campaign_name,
    picture_url: c.image_url ?? undefined,
    start_at: c.use_start_at,
    expired_at: c.use_end_at,
  }));
  return { data: { claimable: coupons.length > 0, coupons } };
};

/** 取本會員最近一筆已歸還紀錄 id(給領券的 rental_log_id 用) */
const fetchLatestReturnedRentalId = async (
  token: string | null
): Promise<number | null> => {
  try {
    const { data } = await mgmtAxios.request<MgmtEnvelope<{ data: { id: number }[] }>>({
      url: `/api/member/rentals/history`,
      method: 'GET',
      params: { per_page: 1 },
      headers: authHeader(token),
    });
    return data.details?.data?.[0]?.id ?? null;
  } catch {
    return null;
  }
};

export interface BindUserRentLogData {
  success: boolean;
  message: string;
  user_rent_logs: object;
}

/**
 * 綁定訪客歸還到會員 +(可選)領券。
 *  - claimTokens 非空 → POST /api/member/rentals/claim(把訪客歸還綁到會員)
 *  - coupon_id 非空 → POST /api/member/coupons/claim(PG-178,rental_log_id 取最近一筆已歸還)
 */
export const bindUserRentLog = async (
  _lineToken: string,
  claimTokens: string[],
  coupon_id?: number[]
): Promise<Response<BindUserRentLogData>> => {
  const token = getMgmtMemberToken();

  // 1) 綁定訪客歸還(有 token 才綁)
  const validTokens = (claimTokens || []).filter(Boolean);
  if (validTokens.length) {
    await mgmtAxios.request({
      url: `/api/member/rentals/claim`,
      method: 'POST',
      data: { claim_tokens: validTokens },
      headers: authHeader(token),
    });
  }

  // 2) 領券(有選券才領;rental_log_id 取本會員最近一筆已歸還)
  if (coupon_id && coupon_id.length) {
    const rentalLogId = await fetchLatestReturnedRentalId(token);
    if (rentalLogId) {
      await mgmtAxios.request({
        url: `/api/member/coupons/claim`,
        method: 'POST',
        data: { rental_log_id: rentalLogId, coupon_ids: coupon_id },
        headers: authHeader(token),
      });
    }
  }

  return { data: { success: true, message: 'OK', user_rent_logs: {} } };
};
